// Recovery for a stale tab after a deploy (OMG-2594, generalising what
// routes.tsx did for its route-level lazy() only).
//
// An open tab keeps referencing the chunk hashes of the index.html it
// loaded. After a deploy those files are gone, so the next dynamic import()
// 404s: React's lazy() would hang on its Suspense fallback forever, and a
// data import inside useSuspenseQuery throws straight to the boundary. Sentry
// NMH-REACT-2S3 ("Importing a module script failed", Safari) and -2B3
// ("Failed to fetch dynamically imported module", Chrome) are exactly the
// imports that never got this recovery: ContentfulProviders, the technique
// JSON, the simulation canvas.
//
// Recovery = catch the chunk-load error and force ONE reload so the tab picks
// up the new index.html. The sessionStorage timestamp prevents an infinite
// reload loop when chunks keep failing for another reason (network, an
// ad-blocker): inside the cooldown the error is rethrown untouched so it
// surfaces normally.
import type { ComponentType } from "react";
import { lazy } from "react";

import { safeSessionStorage } from "@lib/safeStorage";

export const CHUNK_RELOAD_TS_KEY = "chunk-reload-ts";
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/** The three browser engines' wording for a failed dynamic import(). */
export const isChunkLoadError = (err: unknown): boolean => {
  const msg = err instanceof Error ? err.message : String(err);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
    msg,
  );
};

export type ChunkRecoveryDeps = {
  reload: () => void;
  storage: Pick<Storage, "getItem" | "setItem">;
  now: () => number;
};

export const createChunkRecovery = ({
  reload,
  storage,
  now,
}: ChunkRecoveryDeps) => {
  /**
   * For a chunk-load error outside the cooldown: stamp, reload, and return a
   * promise that never settles (the page is going away; resolving would
   * render with a missing module, rejecting would show an error for the
   * half-second before the reload lands). Anything else is rethrown.
   */
  const recover = <T>(err: unknown): Promise<T> => {
    if (isChunkLoadError(err)) {
      // Via safeSessionStorage (OMG-2249): this runs on the one path that is
      // supposed to rescue the member. Raw access meant a storage-blocked
      // browser got a SecurityError in place of the chunk error and a blank
      // page instead of the reload. A blocked store reads as "no previous
      // reload", the right failure direction: the member gets their one try.
      // A non-numeric stamp (hand edit, another script on the key) must read
      // as "never", not NaN: `now - NaN > cooldown` is always false and would
      // silently switch the recovery off for the whole tab session.
      const parsed = Number(storage.getItem(CHUNK_RELOAD_TS_KEY));
      const last = Number.isFinite(parsed) ? parsed : 0;
      if (now() - last > CHUNK_RELOAD_COOLDOWN_MS) {
        storage.setItem(CHUNK_RELOAD_TS_KEY, String(now()));
        reload();
        return new Promise<T>(() => {});
      }
    }
    throw err;
  };

  /** A dynamic import() of anything (a module, a JSON file) with recovery. */
  const importWithReload = <T>(factory: () => Promise<T>): Promise<T> =>
    factory().catch((err: unknown) => recover<T>(err));

  /** React.lazy() with recovery; the route table's wrapper since OMG-2249. */
  const lazyWithReload = <T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
  ) => lazy(() => importWithReload(factory));

  return { recover, importWithReload, lazyWithReload };
};

const production = createChunkRecovery({
  reload: () => window.location.reload(),
  storage: safeSessionStorage,
  now: Date.now,
});

export const { importWithReload, lazyWithReload } = production;
